/* ==========================================================================
   Conectify Tech — Reusable Framer Motion Variants & Curves
   Organic easing, non-jarring distances, and tactile micro-interactions.
   ========================================================================== */

/* ---------- Easing Curves ---------- */
const EASE_SMOOTH = [0.25, 0.1, 0.25, 1]; // Fluid easeOutQuart
const EASE_OUT = [0.215, 0.61, 0.355, 1];  // Soft easeOutCubic

/* ---------- Reveal Animations ---------- */

export const fadeIn = (delay = 0, duration = 0.5) => ({
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration, delay, ease: EASE_SMOOTH },
  },
});

export const fadeUp = (delay = 0, distance = 16) => ({
  hidden: { opacity: 0, y: distance },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: EASE_OUT,
    },
  },
});

export const fadeDown = (delay = 0, distance = 16) => ({
  hidden: { opacity: 0, y: -distance },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: EASE_OUT,
    },
  },
});

export const fadeLeft = (delay = 0, distance = 20) => ({
  hidden: { opacity: 0, x: -distance },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: EASE_OUT,
    },
  },
});

export const fadeRight = (delay = 0, distance = 20) => ({
  hidden: { opacity: 0, x: distance },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: EASE_OUT,
    },
  },
});

export const scaleIn = (delay = 0, from = 0.96) => ({
  hidden: { opacity: 0, scale: from },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      delay,
      ease: EASE_OUT,
    },
  },
});

/* ---------- Stagger & Path Animations ---------- */

export const staggerContainer = (stagger = 0.08, delayChildren = 0.05) => ({
  hidden: {},
  show: {
    transition: {
      staggerChildren: stagger,
      delayChildren,
    },
  },
});

export const drawLine = (delay = 0, duration = 1) => ({
  hidden: { pathLength: 0, opacity: 0 },
  show: {
    pathLength: 1,
    opacity: 1,
    transition: { duration, delay, ease: "easeInOut" },
  },
});

/* ---------- Hover & Tap Micro-Interactions ---------- */

export const hoverCard = {
  rest: {
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 20 },
  },
  hover: {
    y: -5,
    scale: 1.012,
    transition: { type: "spring", stiffness: 380, damping: 18 },
  },
  tap: {
    scale: 0.985,
    transition: { type: "spring", stiffness: 450, damping: 25 },
  },
};

export const hoverButton = {
  hover: {
    y: -2,
    scale: 1.02,
    transition: { type: "spring", stiffness: 400, damping: 15 },
  },
  tap: {
    scale: 0.96,
    transition: { type: "spring", stiffness: 500, damping: 20 },
  },
};

export const hoverIcon = {
  rest: { scale: 1, rotate: 0 },
  hover: {
    scale: 1.12,
    rotate: -4,
    transition: { type: "spring", stiffness: 420, damping: 16 },
  },
};

/* ---------- Ambient / Continuous Motion ---------- */

export const floating = (distance = 8, duration = 4.5) => ({
  animate: {
    y: [0, -distance, 0],
    transition: { duration, repeat: Infinity, ease: "easeInOut" },
  },
});

export const pulseGlow = {
  animate: {
    opacity: [0.5, 0.85, 0.5],
    scale: [1, 1.03, 1],
    transition: { duration: 5, repeat: Infinity, ease: "easeInOut" },
  },
};

/* ---------- Viewport Triggers ---------- */

export const viewportOnce = { once: true, amount: 0.12 };
export const viewportLoose = { once: true, amount: 0.05 };

export const EASE_OUT_EXPO = EASE_SMOOTH;